// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CrewManager, { XP_THRESHOLDS, XP_REWARDS } from '../utils/game/CrewManager';
import CrewGoalsManager from '../utils/game/CrewGoalsManager';
import { getCrewBackstoryInfo, initializeBackstories } from '../utils/game/CrewBackstoryIntegration';
import { useCredits } from '../contexts/CreditsContext';
import useEscapeKey from '../hooks/ui/useEscapeKey';
import ConfirmModal from './ConfirmModal';
import Toast from './Toast';
import CrewDetailModal from './CrewDetailModal';
import './CrewRosterModal.css';

const CrewRosterModal = ({ onClose, embedded = false }) => {
  const [crew, setCrew] = useState([]);
  const [stats, setStats] = useState({});
  const [modalFading, setModalFading] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const { credits, removeCredits } = useCredits();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const refreshCrew = () => {
    const crewData = CrewManager.getAllCrew();
    
    // Initialize goals for crew members who don't have them
    crewData.forEach(member => {
      CrewGoalsManager.initializeGoals(member);
    });
    
    const totalCrew = crewData.length;
    const injured = crewData.filter(c => c.injured).length;
    const averageMorale = CrewManager.getMorale();
    const efficiency = CrewManager.getEfficiency() * 100;
    const bonuses = CrewManager.getBonuses();
    
    const displayBonuses = {
      fuelEfficiency: Math.round((1 - bonuses.fuelEfficiency) * 100),
      repairSpeed: Math.round((bonuses.repairSpeed - 1) * 100),
      weaponDamage: Math.round((bonuses.weaponDamage - 1) * 100),
      healingRate: bonuses.casualtyReduction < 1 ? 50 : 0,
      scanRange: Math.round((bonuses.scanQuality - 1) * 100)
    };
    
    setCrew(crewData);
    setStats({
      totalCrew,
      injured,
      averageMorale,
      efficiency,
      bonuses: displayBonuses
    });
  };

  useEffect(() => {
    // Initialize backstories for existing crew
    initializeBackstories();
    refreshCrew();
  }, []);

  const handleClose = () => {
    if (embedded) {
      onClose();
      return;
    }
    setModalFading(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEscapeKey(handleClose);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleCrewClick = (member) => {
    setSelectedCrew(member);
    setShowDetailModal(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleTrain = (crewId) => {
    const trainingCost = 500;
    if (credits < trainingCost) {
      showToast('Insufficient credits for training!\nCost: 500 credits', 'error');
      return;
    }

    removeCredits(trainingCost);
    const leveledUp = CrewManager.awardExperience(crewId, XP_REWARDS.TRAINING);
    
    if (leveledUp.length > 0) {
      showToast(`🎉 ${leveledUp[0].name} leveled up to ${leveledUp[0].level}!`, 'success');
    } else {
      showToast('Training complete! +50 XP', 'success');
    }
    
    refreshCrew();
    if (selectedCrew) {
      setSelectedCrew(CrewManager.getCrewMember(crewId));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleFire = (crewId) => {
    setConfirmModal({
      isOpen: true,
      title: '⚠️ FIRE CREW MEMBER',
      message: 'Are you sure you want to fire this crew member?\nThis action cannot be undone.',
      onConfirm: () => {
        const result = CrewManager.fireCrew(crewId);
        showToast(result.message, result.success ? 'success' : 'error');
        if (result.success) {
          setSelectedCrew(null);
          refreshCrew();
        }
        setConfirmModal({ isOpen: false });
      }
    });
  };

  const getRoleIcon = (roleData) => roleData?.icon || '👤';
  const getRoleColor = (roleData) => {
    const colors = {
      pilot: '#00ffff',
      engineer: '#ffaa00',
      gunner: '#ff4444',
      medic: '#00ff88',
      scientist: '#ff00ff',
      generic: '#88ccff'
    };
    return colors[roleData?.id] || '#88ccff';
  };

  const getExperienceStars = (stars) => '⭐'.repeat(stars || 1);

  const getMoraleColor = (morale) => {
    if (morale >= 80) return '#00ff88';
    if (morale >= 60) return '#ffaa00';
    if (morale >= 40) return '#ff9900';
    return '#ff4444';
  };

  const getMoraleLabel = (morale) => {
    if (morale >= 80) return 'EXCELLENT';
    if (morale >= 60) return 'GOOD';
    if (morale >= 40) return 'LOW';
    if (morale >= 20) return 'CRITICAL';
    return 'MUTINY RISK';
  };

  const getNextLevelXP = (currentLevel) => {
    const levels = ['ROOKIE', 'TRAINED', 'EXPERIENCED', 'VETERAN', 'ELITE'];
    const currentIndex = levels.indexOf(currentLevel);
    return currentIndex < levels.length - 1 ? XP_THRESHOLDS[levels[currentIndex + 1]] : null;
  };

  const content = (
    <>
      <div className="crew-roster-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crew-roster-header">
          <h2>◊ CREW ROSTER ◊</h2>
          {!embedded && <button className="close-button" onClick={handleClose}>✕</button>}
        </div>

        <div className="crew-stats-overview">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <div className="stat-label">TOTAL CREW</div>
              <div className="stat-value">{stats.totalCrew || 0}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⚕️</div>
            <div className="stat-info">
              <div className="stat-label">INJURED</div>
              <div className="stat-value" style={{ color: stats.injured > 0 ? '#ff4444' : '#00ff88' }}>
                {stats.injured || 0}
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">😊</div>
            <div className="stat-info">
              <div className="stat-label">MORALE</div>
              <div className="stat-value" style={{ color: getMoraleColor(stats.averageMorale || 100) }}>
                {Math.round(stats.averageMorale || 100)}%
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-info">
              <div className="stat-label">EFFICIENCY</div>
              <div className="stat-value" style={{ color: getMoraleColor(stats.efficiency || 100) }}>
                {Math.round(stats.efficiency || 100)}%
              </div>
            </div>
          </div>
        </div>

        <div className="overall-morale">
          <div className="morale-label">
            CREW MORALE: {getMoraleLabel(stats.averageMorale || 100)}
          </div>
          <div className="morale-bar">
            <div 
              className="morale-fill"
              style={{ 
                width: `${stats.averageMorale || 100}%`,
                background: `linear-gradient(90deg, ${getMoraleColor(stats.averageMorale || 100)}40, ${getMoraleColor(stats.averageMorale || 100)})`,
                boxShadow: `0 0 10px ${getMoraleColor(stats.averageMorale || 100)}80`
              }}
            />
          </div>
        </div>

        {stats.bonuses && Object.values(stats.bonuses).some(b => b > 0) && (
          <div className="active-bonuses">
            <h3>◊ ACTIVE BONUSES ◊</h3>
            <div className="bonus-grid">
              {stats.bonuses.fuelEfficiency > 0 && (
                <div className="bonus-item">
                  <span className="bonus-icon">⚡</span>
                  <span className="bonus-text">Fuel Efficiency +{stats.bonuses.fuelEfficiency}%</span>
                </div>
              )}
              {stats.bonuses.repairSpeed > 0 && (
                <div className="bonus-item">
                  <span className="bonus-icon">🔧</span>
                  <span className="bonus-text">Repair Speed +{stats.bonuses.repairSpeed}%</span>
                </div>
              )}
              {stats.bonuses.weaponDamage > 0 && (
                <div className="bonus-item">
                  <span className="bonus-icon">🎯</span>
                  <span className="bonus-text">Weapon Damage +{stats.bonuses.weaponDamage}%</span>
                </div>
              )}
              {stats.bonuses.healingRate > 0 && (
                <div className="bonus-item">
                  <span className="bonus-icon">⚕️</span>
                  <span className="bonus-text">Healing Rate +{stats.bonuses.healingRate}%</span>
                </div>
              )}
              {stats.bonuses.scanRange > 0 && (
                <div className="bonus-item">
                  <span className="bonus-icon">📡</span>
                  <span className="bonus-text">Scan Range +{stats.bonuses.scanRange}%</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="crew-list-container">
          <h3>◊ CREW MEMBERS ◊</h3>
          <div className="crew-list">
            {crew.map((member) => (
              <div key={member.id} className="crew-member-card" onClick={() => handleCrewClick(member)}>
                <div className="crew-member-header">
                  <div className="crew-member-icon" style={{ color: getRoleColor(member.roleData) }}>
                    {getRoleIcon(member.roleData)}
                  </div>
                  <div className="crew-member-info">
                    <div className="crew-member-name">{member.name}</div>
                    <div className="crew-member-role" style={{ color: getRoleColor(member.roleData) }}>
                      {member.roleData?.name || 'Unknown'}
                      {member.speciesData && ` • ${member.speciesData.name}`}
                    </div>
                  </div>
                  <div className="crew-member-experience">
                    {getExperienceStars(member.experienceData?.stars)}
                  </div>
                </div>

                <div className="crew-member-stats">
                  <div className="crew-stat">
                    <span className="crew-stat-label">XP:</span>
                    <div className="crew-stat-bar">
                      <div 
                        className="crew-stat-fill"
                        style={{ 
                          width: `${getNextLevelXP(member.experience) ? (member.xp / getNextLevelXP(member.experience) * 100) : 100}%`,
                          background: '#00ffff'
                        }}
                      />
                    </div>
                    <span className="crew-stat-value">
                      {member.xp} / {getNextLevelXP(member.experience) || 'MAX'}
                    </span>
                  </div>

                  {member.injured && (
                    <div className="crew-injured-badge">
                      ⚕️ INJURED
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {!embedded && (
          <div className="crew-roster-footer">
            <div className="footer-note">Visit stations to hire more crew</div>
            <button className="close-modal-button" onClick={handleClose}>
              CLOSE
            </button>
          </div>
        )}
      </div>

      {showDetailModal && selectedCrew && (
        <CrewDetailModal
          crewMember={selectedCrew}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCrew(null);
          }}
          getCrewBackstoryInfo={getCrewBackstoryInfo}
          CrewGoalsManager={CrewGoalsManager}
          onTrainingStarted={(message) => {
            showToast(message, 'success');
            refreshCrew();
          }}
          onFireCrew={(crewId) => {
            const result = CrewManager.fireCrew(crewId);
            showToast(result.message, result.success ? 'success' : 'error');
            if (result.success) {
              refreshCrew();
            }
          }}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false })}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return createPortal(
    <div 
      className={`crew-roster-modal-overlay ${modalFading ? 'fading' : ''}`}
      onClick={handleOverlayClick}
    >
      {content}
    </div>,
    document.body
  );
};

export default CrewRosterModal;
